import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, Plus, ChevronRight, Rocket } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLE = {
  DRAFT:    { bg: '#F3F4F6', fg: '#6B7280' },
  READY:    { bg: '#D1FAE5', fg: '#059669' },
  DEPLOYED: { bg: '#D1FAE5', fg: '#059669' },
  ACTIVE:   { bg: '#DBEAFE', fg: '#2563EB' },
};

const ContractsPage = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const filter = (search.get('filter') || '').toLowerCase(); // '', 'used', 'remaining'
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState({ base_contracts: 10, contracts_used: 0, contracts_remaining: 10 });
  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/creator/billing`, { headers }),
      axios.get(`${API}/contracts/drafts`, { headers }),
    ]).then(([b, d]) => {
      setBilling({
        base_contracts: b.data.base_contracts ?? 10,
        contracts_used: b.data.contracts_used ?? 0,
        contracts_remaining: b.data.contracts_remaining ?? 0,
      });
      setDrafts(d.data?.drafts || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const shown = (() => {
    if (filter === 'used') return drafts;
    if (filter === 'remaining') return [];
    return drafts;
  })();

  return (
    <div data-testid="contracts-page" style={{ padding: '40px', maxWidth: '1100px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.text.primary, fontFamily: theme.fonts.heading, marginBottom: '6px' }}>
        {filter === 'used' ? 'Used Contracts' : filter === 'remaining' ? 'Remaining Quota' : 'Contracts & Quota'}
      </h1>
      <p style={{ fontSize: '15px', color: theme.colors.text.secondary, marginBottom: '24px' }}>
        {filter === 'used'
          ? `${billing.contracts_used} of ${billing.base_contracts} contracts used`
          : filter === 'remaining'
          ? `${billing.contracts_remaining} contracts remaining — start a new one below`
          : 'Your contract drafts and quota status'}
      </p>

      {/* Quota strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '28px' }}>
        <button onClick={() => navigate('/creator/contracts?filter=used')} data-testid="quota-used-chip"
          style={{ padding: '14px 16px', background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, cursor: 'pointer', textAlign: 'left' }}>
          <p style={{ fontSize: '12px', color: theme.colors.text.muted, margin: 0 }}>Used</p>
          <p style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.primary, margin: 0 }}>{billing.contracts_used}</p>
        </button>
        <button onClick={() => navigate('/creator/contracts?filter=remaining')} data-testid="quota-remaining-chip"
          style={{ padding: '14px 16px', background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, cursor: 'pointer', textAlign: 'left' }}>
          <p style={{ fontSize: '12px', color: theme.colors.text.muted, margin: 0 }}>Remaining</p>
          <p style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.status.success, margin: 0 }}>{billing.contracts_remaining}</p>
        </button>
        <div style={{ padding: '14px 16px', background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md }}>
          <p style={{ fontSize: '12px', color: theme.colors.text.muted, margin: 0 }}>Total</p>
          <p style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, margin: 0 }}>{billing.base_contracts}</p>
        </div>
      </div>

      {filter === 'remaining' && (
        <button data-testid="create-contract-cta" onClick={() => navigate('/creator/contracts/templates')}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 18px', background: theme.colors.primary, color: '#FFF', border: 'none', borderRadius: theme.radius.sm, cursor: 'pointer', fontWeight: 600, marginBottom: '24px' }}>
          <Plus size={16} /> Create Contract
        </button>
      )}

      {loading ? (
        <div style={{ padding: '40px', color: theme.colors.text.muted }}>Loading…</div>
      ) : shown.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <FileText size={32} color={theme.colors.text.muted} />
          <p style={{ marginTop: '10px', color: theme.colors.text.muted, fontSize: '14px' }}>
            {filter === 'remaining' ? 'Pick a template above to start a new contract.' : 'No contract drafts yet.'}
          </p>
        </div>
      ) : (
        <div data-testid="contracts-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {shown.map(d => {
            const st = STATUS_STYLE[d.status] || STATUS_STYLE.DRAFT;
            const deployed = !!(d.deployed || d.contract_address);
            return (
              <div key={d.id} data-testid={`contract-row-${d.id}`}
                onClick={() => navigate(`/creator/contracts/${d.id}`)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = theme.colors.shadows.sm}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FileText size={18} color={theme.colors.primary} />
                  <div>
                    <p style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
                      {d.params?.contract_name || d.template_name || 'Untitled'}
                    </p>
                    <p style={{ fontSize: '12px', color: theme.colors.text.muted, margin: '2px 0 0' }}>
                      {d.standard} · {d.template_name} · {new Date(d.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, background: st.bg, color: st.fg }}>{d.status}</span>
                  {!deployed && d.status === 'DRAFT' && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: theme.colors.primary, fontWeight: 600 }}>
                      <Rocket size={12} /> Deploy
                    </span>
                  )}
                  <ChevronRight size={16} color={theme.colors.text.muted} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContractsPage;
